/** Shared HTML normalization for React ↔ SSR parity checks. */

const ATTR_RE = /([^\s=]+)(?:="([^"]*)")?/g;

/** HTML boolean attrs — React may emit camelCase + `=""`, SSR emits lowercase bare. */
const BOOLEAN_ATTRS = new Set([
  'allowfullscreen',
  'async',
  'autofocus',
  'autoplay',
  'checked',
  'controls',
  'default',
  'defer',
  'disabled',
  'formnovalidate',
  'hidden',
  'ismap',
  'loop',
  'multiple',
  'muted',
  'nomodule',
  'novalidate',
  'open',
  'playsinline',
  'readonly',
  'required',
  'reversed',
  'selected',
]);

function normalizeTagAttributes(rawAttrs: string): string {
  const attrs: { name: string; value: string | null }[] = [];
  const trimmed = rawAttrs.trim();
  if (!trimmed) return '';

  ATTR_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = ATTR_RE.exec(trimmed)) !== null) {
    const rawName = match[1];
    if (!rawName) continue;
    const name = rawName.toLowerCase();
    let value: string | null = match[2] !== undefined ? match[2] : null;
    if (BOOLEAN_ATTRS.has(name) && (value === null || value === '' || value === name)) {
      value = null;
    }
    attrs.push({ name, value });
  }

  attrs.sort((a, b) => a.name.localeCompare(b.name));
  return attrs
    .map(({ name, value }) => (value === null ? name : `${name}="${value}"`))
    .join(' ');
}

function normalizeTags(html: string): string {
  return html.replace(
    /<([a-zA-Z][\w:-]*)(\s[^>]*?)?(\s*\/)?>/g,
    (_full, tag, attrs = '', close = '') => {
      const normalizedClose = close.includes('/') ? ' /' : '';
      const normalizedAttrs = normalizeTagAttributes(attrs);
      return normalizedAttrs
        ? `<${tag} ${normalizedAttrs}${normalizedClose}>`
        : `<${tag}${normalizedClose}>`;
    },
  );
}

/**
 * Normalize markup so whitespace / attribute-order noise does not create false mismatches.
 * Does not strip studio edit chrome — call `stripStudioEditChrome` first when comparing canvas HTML.
 */
export function normalizeParityHtml(html: string): string {
  return normalizeTags(
    html
      .replace(/\s+/g, ' ')
      .replace(/>\s+</g, '><')
      .replace(/\s+\/>/g, '/>')
      .trim(),
  );
}

/** Inner HTML of `#ws-site-root`, ignoring React DOM preload `<link>` siblings. */
export function extractWsSiteRootInnerHtml(markup: string): string {
  const match = markup.match(/id="ws-site-root"[^>]*>([\s\S]*)<\/div>\s*$/i);
  return match?.[1]?.trim() ?? '';
}

export function extractMainMarkup(fullDocument: string): string {
  const match = fullDocument.match(/<main[^>]*>([\s\S]*?)<\/main>/i);
  return match?.[1]?.trim() ?? '';
}

/**
 * Remove Design Lab / studio edit-mode chrome so canvas HTML can be compared to SSR.
 * Unwraps legacy `ws-edit-media` spans (class moves onto the inner img) and drops edit data attrs.
 * Current EditableMedia is a single img — class stripping below is enough for that path.
 */
export function stripStudioEditChrome(html: string): string {
  let out = html.replace(
    /<span\b([^>]*\bws-edit-media\b[^>]*)>([\s\S]*?)<\/span>/gi,
    (_full, spanAttrs: string, inner: string) => {
      const classMatch = spanAttrs.match(/\bclass="([^"]*)"/i);
      const wrapperClasses = (classMatch?.[1] ?? '')
        .split(/\s+/)
        .filter((c) => c && c !== 'ws-edit-media' && !c.startsWith('ws-edit-'));
      return inner.replace(/<img\b([^>]*)>/i, (_imgFull, imgAttrs: string) => {
        let attrs = imgAttrs
          .replace(/\sclass="([^"]*)"/i, (_c: string, existing: string) => {
            const kept = existing
              .split(/\s+/)
              .filter((c) => c && c !== 'ws-edit-media__img' && !c.startsWith('ws-edit-'));
            const merged = [...wrapperClasses, ...kept].filter(Boolean);
            return merged.length ? ` class="${merged.join(' ')}"` : '';
          });
        if (wrapperClasses.length && !/\sclass="/i.test(imgAttrs)) {
          attrs += ` class="${wrapperClasses.join(' ')}"`;
        }
        return `<img${attrs}>`;
      });
    },
  );

  out = out
    .replace(/\s+data-edit-child="[^"]*"/gi, '')
    .replace(/\s+data-field-key="[^"]*"/gi, '')
    .replace(/\s+data-media-active="[^"]*"/gi, '')
    .replace(/\sclass="([^"]*)"/gi, (_full, classList: string) => {
      const kept = classList
        .split(/\s+/)
        .filter((c) => c && !c.startsWith('ws-edit-') && c !== 'ws-site--editable');
      return kept.length ? ` class="${kept.join(' ')}"` : '';
    });

  return out;
}

export interface BlockParityIdentity {
  blockId: string;
  componentType: string;
}

const BLOCK_IDENTITY_RE =
  /data-block-id="([^"]+)"[^>]*data-component="([^"]+)"|data-component="([^"]+)"[^>]*data-block-id="([^"]+)"/g;

/** Ordered block identities from markup (React SitePage or SSR main). */
export function extractBlockParityIdentities(html: string): BlockParityIdentity[] {
  const identities: BlockParityIdentity[] = [];
  for (const match of html.matchAll(BLOCK_IDENTITY_RE)) {
    const blockId = match[1] ?? match[4];
    const componentType = match[2] ?? match[3];
    if (blockId && componentType) {
      identities.push({ blockId, componentType });
    }
  }
  return identities;
}

export function blockParityIdentitiesEqual(
  a: readonly BlockParityIdentity[],
  b: readonly BlockParityIdentity[],
): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every(
    (entry, index) =>
      entry.blockId === b[index]?.blockId && entry.componentType === b[index]?.componentType,
  );
}
